import React, { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { isChunkLoadError } from "../AppErrorBoundary";

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundaryV3 extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("ErrorBoundaryV3 caught:", error, errorInfo);
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Let the app boundary own stale-chunk recovery and its reload-loop guard.
      // Errors thrown while rendering this boundary are caught by its parent.
      if (this.state.error && isChunkLoadError(this.state.error)) {
        throw this.state.error;
      }

      return (
        <div className="flex flex-col items-center justify-center py-16 px-6">
          <AlertTriangle className="w-10 h-10 text-rp-red mb-4" />
          <h3 className="text-lg font-bold text-rp-grey mb-2">
            {this.props.fallbackTitle ?? "Something went wrong"}
          </h3>
          <p className="text-sm text-rp-grey mb-4 text-center max-w-md">
            {this.state.error?.message ?? "An unexpected error occurred. Please try again."}
          </p>
          <button
            onClick={this.handleReset}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-rp-separator text-rp-grey hover:bg-rp-separator border border-rp-separator transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundaryV3;
